import { useEffect, useState } from 'react';
import type { PublicConfig } from './types';
import { DEFAULT_CONFIG } from './constants';
import { getPublicConfig } from './services/config';
import { track } from './services/events';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useUrlView } from './hooks/useUrlView';
import { AppToastProvider, useToast } from './components/feedback/AppToast';
import { AppHeader } from './components/layout/AppHeader';
import { HeroSection } from './components/home/HeroSection';
import { EvaluationPanel } from './components/evaluation/EvaluationPanel';
import { AuthModal } from './components/auth/AuthModal';
import { ReportPage } from './components/reports/ReportPage';

function AppContent() {
  const { view, reportId, navigate } = useUrlView();
  const auth = useAuth();
  const toast = useToast();
  const [config, setConfig] = useState<PublicConfig>(DEFAULT_CONFIG);
  useEffect(() => {
    const c = new AbortController();
    getPublicConfig(c.signal).then(setConfig);
    track('page_view', { view });
    return () => c.abort();
  }, []);
  const logout = async () => {
    try {
      await auth.logout();
      navigate('evaluate');
      toast('已安全退出', 'success');
    } catch (e) {
      toast(e instanceof Error ? e.message : '退出失败', 'error');
    }
  };
  const scrollToTool = () =>
    document
      .getElementById('evaluation-tool')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return (
    <div className="page">
      <AppHeader
        view={view}
        user={auth.user}
        authLoading={auth.loading}
        onNavigate={navigate}
        onLogin={auth.openAuth}
        onLogout={logout}
      />
      {view === 'evaluate' ? (
        <main>
          <HeroSection onStart={scrollToTool} />
          <EvaluationPanel config={config} onViewReports={(id) => navigate('reports', id)} />
        </main>
      ) : (
        <ReportPage
          reportId={reportId}
          onSelect={(id) => navigate('reports', id)}
          onBackToEvaluate={() => navigate('evaluate')}
        />
      )}
      <footer className="footer container">
        <span>GEO文章评测 · 独立认证与数据空间</span>
        <a href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer">
          京ICP备2021036909号-16
        </a>
      </footer>
      <AuthModal />
    </div>
  );
}

export default function App() {
  return (
    <AppToastProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </AppToastProvider>
  );
}
